import React, { useEffect } from 'react';
import { translate } from 'react-i18next';
import { Linking, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';
import { scale } from 'react-native-size-matters';
import { connect } from 'react-redux';
import {
  VideoToggleButton,
} from './VideoCalling/VideoCallConrolToggleButtons';
import Theme from '../../theme';
import { defaultScale } from '../../utils/scaling';
import { closeSetupScreen, disableMirror, enableMirror, mirrorSetupScreenOpened } from '../actions';
import { IAppStore, Orientation } from '../reducer';
import { orientationSelector } from '../selectors';
import { DeviceSetupSubScreenHeader } from './DeviceSetup/DeviceSetupSubScreenHeader';
import { HomeWorkoutResponsiveLayout } from './HomeWorkoutResponsiveLayout';
import { SelfViewComponent } from './SelfView';

interface IProps {
  orientation: Orientation;
  mirrorIsActive: boolean;
  hasCameraPermission: boolean;
  t(key: string, options?: any): string;
  closeSetupScreen(): void;
  disableMirror(): void;
  enableMirror(): void;
  mirrorSetupOpened(): void;
}

const VideoMirror = ({
  t,
  orientation,
  mirrorIsActive,
  closeSetupScreen,
  enableMirror,
  disableMirror,
  hasCameraPermission,
  mirrorSetupOpened,
}: IProps) => {
  const isPortrait = orientation === 'PORTRAIT';
  const videoCallContainer = isPortrait
  ? styles.videoCallContainerPortrait
  : styles.videoCallContainerLandscape;

  const infoContainer = isPortrait
  ? styles.infoContainerPortrait
  : styles.infoContainerLandscape;

  useEffect(() => {
    mirrorSetupOpened();
  });

  const mirrorStateHandler = mirrorIsActive ? disableMirror : enableMirror;
  return (
    <>
      <DeviceSetupSubScreenHeader title={t('exerciseMirror')} onBackPress={closeSetupScreen} />
      <View style={styles.container}>
        <HomeWorkoutResponsiveLayout>
          <View style={videoCallContainer}>
            <View style={styles.videoCallDisplay}>
              {!mirrorIsActive &&
                <View style={styles.videoCallDisconnectedContainer}>
                  {
                    !hasCameraPermission &&
                      <TouchableOpacity onPress={() => Linking.openSettings()}>
                          <Text style={styles.videoCallDisconnected}>
                            {t('cameraPermissionsErrorBeforeUnderline', {feature: 'exercise mirror'})}
                          <Text style={[styles.videoCallDisconnected, { textDecorationLine: 'underline' }]}>
                        {t('cameraPermissionsErrorUnderline')}
                          </Text>
                        {t('cameraPermissionsErrorAfterUnderline')}</Text>
                      </TouchableOpacity>
                  }
                </View>
              }
              {mirrorIsActive &&
                <View style={styles.mirrorContainer}>
                <SelfViewComponent customStyle={styles.videoMirrorContainer}/>
                </View>
              }
            </View>
              <View style={styles.videoControlStrip}>
                <TouchableOpacity onPress={mirrorStateHandler} style={styles.videoButton}>
                  <VideoToggleButton onPress={mirrorStateHandler} localVideoIsMuted={!mirrorIsActive} />
                  <Text style={[styles.videoButtonText, { color: Theme.PRIMARY }]}>
                    {mirrorIsActive ? t('stopMirror') : t('startMirror')}
                  </Text>
                </TouchableOpacity>
              </View>
          </View>
          <View style={infoContainer}>
            <ScrollView contentContainerStyle={styles.mirrorTextContainer}>
              <View>
                <Text style={[styles.VideoMirrorAllows, {paddingBottom: 10}]}>{t('VideoMirrorAllows')}</Text>
                <Text style={styles.VideoMirrorAllows}>{t('VideoMirrorPrivacy')}</Text>
              </View>
            </ScrollView>
          </View>
        </HomeWorkoutResponsiveLayout>
      </View>
    </>
  );
};

const styles = StyleSheet.create({
  mirrorContainer: {
    flex: 1,
  },
  container: {
    flex: 1,
    flexDirection: 'row',
  },
  videoCallContainerPortrait: {
    flex: 0.7,
  },
  videoCallContainerLandscape: {
    flex: 1,
  },
  videoCallDisplay: {
    flex: 1,
  },
  videoCallDisconnectedContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: Theme.CHARCOAL,
  },
  videoCallDisconnected: {
    fontSize: 18,
    color: Theme.WHITE,
    fontFamily: Theme.Custom800,
    paddingHorizontal: defaultScale(20),
  },
  videoControlStrip: {
    height: scale(60),
    flexDirection: 'row',
    backgroundColor: Theme.GREY_DARK,
  },
  videoButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  videoButtonText: {
    ...Theme.textStyle.heading,
    paddingLeft: defaultScale(10),
  },
  VideoMirrorAllows: {
    ...Theme.textStyle.bodyCopyReg,
    color: Theme.WHITE,
  },
  mirrorTextContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  infoContainerPortrait: {
    flex: 0.3,
    padding: defaultScale(15),
  },
  infoContainerLandscape: {
    flex: 1,
    padding: defaultScale(15),
  },
  videoMirrorContainer: {
    flex: 1,
    overflow: 'hidden',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
});

const mapStateToProps = (state: IAppStore): Partial<IProps> => {
  return {
    orientation: orientationSelector(state),
    mirrorIsActive: state.homeWorkouts.mirrorIsActive,
    hasCameraPermission: state.homeWorkouts.hasCameraPermission,
  };
};

const mapDispatchToProps = (dispatch: any): Partial<IProps> => {
  return {
    disableMirror: () => dispatch(disableMirror()),
    enableMirror: () => dispatch(enableMirror()),
    closeSetupScreen: () => dispatch(closeSetupScreen()),
    mirrorSetupOpened: () => dispatch(mirrorSetupScreenOpened()),
  };
};

export const MirroringSetup = connect(mapStateToProps, mapDispatchToProps)(
  translate(['homeWorkouts'])(VideoMirror));
